/**
 * SDL Chat Database — Dexie.js IndexedDB wrapper
 *
 * Stores chat messages and conversation metadata locally per player account.
 * Compound indexes [localPlayerId+peerId] enable multi-account isolation.
 */

import Dexie, { type Table } from 'dexie';
import type { ChatMessageType } from '@sdl/shared';

// ============================================================================
// INTERFACES
// ============================================================================

/** A chat message stored locally */
export interface StoredMessage {
  /** Server-assigned message ID (e.g. msg_<timestamp>_<rand>) */
  id: string;
  /** Current signed-in player's ID (for multi-account isolation) */
  localPlayerId: string;
  /** The other player in the conversation */
  peerId: string;
  /** Who sent this message */
  from: string;
  fromUsername: string;
  fromPicture?: string;
  /** Message content (text, CDN URL for images, or JSON for invites) */
  content: string;
  type: ChatMessageType;
  timestamp: number;
  /** 0 = unread, 1 = read */
  read: 0 | 1;
  /** Local blob URL for downloaded chat images */
  imageLocalBlob?: string;
  /** The actual image bytes, stored locally once the photo is delivered.
   *  The CDN copy is deleted after this lands (server keeps nothing). */
  imageBlob?: Blob;
  /** Size of imageBlob in bytes (kept flat so stats don't touch the blob). */
  imageBytes?: number;
  /** 1 = photo blob was evicted to reclaim space; show a placeholder. */
  imageEvicted?: 0 | 1;
}

/** Denormalized conversation metadata for the sidebar */
export interface Conversation {
  /** Current signed-in player's ID */
  localPlayerId: string;
  /** The other player's ID */
  peerId: string;
  peerUsername: string;
  peerPicture: string;
  lastMessageContent: string;
  lastMessageType: ChatMessageType;
  lastMessageTimestamp: number;
  unreadCount: number;
}

// ============================================================================
// DATABASE
// ============================================================================

class ChatDatabase extends Dexie {
  messages!: Table<StoredMessage>;
  conversations!: Table<Conversation>;

  constructor() {
    super('sdl-chat');

    this.version(1).stores({
      // Primary key: id
      // Compound indexes for per-conversation and per-account queries
      messages: 'id, [localPlayerId+peerId], [localPlayerId+peerId+timestamp], [localPlayerId+read]',
      // Compound primary key: [localPlayerId+peerId]
      conversations: '[localPlayerId+peerId], [localPlayerId+lastMessageTimestamp]',
    });
  }
}

/** Singleton database instance */
export const chatDb = new ChatDatabase();

// ============================================================================
// OPERATIONS
// ============================================================================

/**
 * Get all conversations for the current player, sorted by most recent first.
 */
export async function getConversations(localPlayerId: string): Promise<Conversation[]> {
  return chatDb.conversations
    .where('[localPlayerId+lastMessageTimestamp]')
    .between([localPlayerId, Dexie.minKey], [localPlayerId, Dexie.maxKey])
    .reverse()
    .toArray();
}

/**
 * Get messages for a specific conversation, paginated.
 * Returns newest-first; caller should reverse for display.
 */
export async function getMessages(
  localPlayerId: string,
  peerId: string,
  limit = 50,
  beforeTimestamp?: number,
): Promise<StoredMessage[]> {
  let collection = chatDb.messages
    .where('[localPlayerId+peerId+timestamp]')
    .between(
      [localPlayerId, peerId, Dexie.minKey],
      [localPlayerId, peerId, beforeTimestamp ?? Dexie.maxKey],
      true,
      beforeTimestamp ? false : true, // exclude the exact beforeTimestamp
    );

  return collection
    .reverse()
    .limit(limit)
    .toArray();
}

/**
 * Save a message and update the conversation metadata.
 */
export async function saveMessage(
  localPlayerId: string,
  msg: StoredMessage,
  peerUsername: string,
  peerPicture: string,
): Promise<void> {
  await chatDb.transaction('rw', chatDb.messages, chatDb.conversations, async () => {
    // Save the message
    await chatDb.messages.put(msg);

    // Upsert conversation
    const key = [localPlayerId, msg.peerId] as [string, string];
    const existing = await chatDb.conversations.get(key);

    if (existing) {
      await chatDb.conversations.update(key, {
        lastMessageContent: msg.content,
        lastMessageType: msg.type,
        lastMessageTimestamp: msg.timestamp,
        peerUsername,
        peerPicture,
        unreadCount: msg.read === 0 ? existing.unreadCount + 1 : existing.unreadCount,
      });
    } else {
      await chatDb.conversations.add({
        localPlayerId,
        peerId: msg.peerId,
        peerUsername,
        peerPicture,
        lastMessageContent: msg.content,
        lastMessageType: msg.type,
        lastMessageTimestamp: msg.timestamp,
        unreadCount: msg.read === 0 ? 1 : 0,
      });
    }
  });
}

/**
 * Delete all messages and conversation metadata for a peer.
 */
export async function deleteConversation(
  localPlayerId: string,
  peerId: string,
): Promise<void> {
  await chatDb.transaction('rw', chatDb.messages, chatDb.conversations, async () => {
    await chatDb.messages
      .where('[localPlayerId+peerId]')
      .equals([localPlayerId, peerId])
      .delete();

    await chatDb.conversations.delete([localPlayerId, peerId]);
  });
}

/**
 * Mark all messages from a peer as read and reset unread count.
 */
export async function markConversationRead(
  localPlayerId: string,
  peerId: string,
): Promise<void> {
  await chatDb.transaction('rw', chatDb.messages, chatDb.conversations, async () => {
    // Update all unread messages
    const unread = await chatDb.messages
      .where('[localPlayerId+peerId]')
      .equals([localPlayerId, peerId])
      .filter(m => m.read === 0)
      .toArray();

    for (const msg of unread) {
      await chatDb.messages.update(msg.id, { read: 1 });
    }

    // Reset conversation unread count
    await chatDb.conversations.update([localPlayerId, peerId], {
      unreadCount: 0,
    });
  });
}

/**
 * Get total unread message count across all conversations.
 */
export async function getUnreadCount(localPlayerId: string): Promise<number> {
  const conversations = await chatDb.conversations
    .where('[localPlayerId+lastMessageTimestamp]')
    .between([localPlayerId, Dexie.minKey], [localPlayerId, Dexie.maxKey])
    .toArray();

  return conversations.reduce((sum, c) => sum + c.unreadCount, 0);
}

// ============================================================================
// STORAGE ACCOUNTING + EVICTION
// ============================================================================

export interface ChatStorageStats {
  /** Approximate total chat bytes (text + photo blobs). */
  totalBytes: number;
  /** Bytes held by stored photo blobs alone. */
  imageBytes: number;
  messageCount: number;
  imageCount: number;
}

/**
 * Approximate storage used by this player's chat history. Text is estimated
 * at 2 bytes/char (UTF-16); photos use the recorded imageBytes so the blobs
 * themselves never get pulled out of IndexedDB for the count.
 */
export async function getChatStorageStats(localPlayerId: string): Promise<ChatStorageStats> {
  const stats: ChatStorageStats = { totalBytes: 0, imageBytes: 0, messageCount: 0, imageCount: 0 };
  await chatDb.messages
    .where('[localPlayerId+peerId]')
    .between([localPlayerId, Dexie.minKey], [localPlayerId, Dexie.maxKey])
    .each((msg) => {
      stats.messageCount++;
      stats.totalBytes += (msg.content?.length ?? 0) * 2;
      if (msg.imageBytes) {
        stats.imageCount++;
        stats.imageBytes += msg.imageBytes;
        stats.totalBytes += msg.imageBytes;
      }
    });
  return stats;
}

/**
 * Evict the oldest photo blobs until at least `bytesToFree` are reclaimed.
 * The message row survives with imageEvicted=1 so the bubble can show a
 * "photo expired" placeholder instead of a broken image.
 * Returns the number of bytes actually freed.
 */
export async function evictOldestImageBlobs(
  localPlayerId: string,
  bytesToFree: number,
): Promise<number> {
  let freed = 0;
  // Oldest-first over the per-account timestamp index; stop once enough
  // photo bytes have been reclaimed.
  const candidates = await chatDb.messages
    .where('[localPlayerId+peerId]')
    .between([localPlayerId, Dexie.minKey], [localPlayerId, Dexie.maxKey])
    .filter(m => !!m.imageBytes && m.imageEvicted !== 1)
    .toArray();
  candidates.sort((a, b) => a.timestamp - b.timestamp);

  for (const msg of candidates) {
    if (freed >= bytesToFree) break;
    await chatDb.messages.update(msg.id, {
      imageBlob: undefined,
      imageEvicted: 1,
    });
    freed += msg.imageBytes ?? 0;
  }
  return freed;
}

/**
 * Delete ALL chat data (messages + conversations) for this player.
 * Used by the Settings "clear chat history" action.
 */
export async function clearAllChatData(localPlayerId: string): Promise<void> {
  await chatDb.transaction('rw', chatDb.messages, chatDb.conversations, async () => {
    await chatDb.messages
      .where('[localPlayerId+peerId]')
      .between([localPlayerId, Dexie.minKey], [localPlayerId, Dexie.maxKey])
      .delete();
    await chatDb.conversations
      .where('[localPlayerId+peerId]')
      .between([localPlayerId, Dexie.minKey], [localPlayerId, Dexie.maxKey])
      .delete();
  });
}

/** Attach a downloaded photo blob to an already-saved message. */
export async function attachImageBlob(messageId: string, blob: Blob): Promise<void> {
  await chatDb.messages.update(messageId, {
    imageBlob: blob,
    imageBytes: blob.size,
    imageEvicted: 0,
  });
}
